import type {
  BreadcrumbList,
  FAQPage,
  ListItem,
  MedicalBusinessLeaf,
  MedicalOrganizationLeaf,
  MergeLeafTypes,
  Thing,
  WebPage,
  WebSite,
} from 'schema-dts';
import { business, organizationStructuredDescription } from '@/config/business';
import { faqsByPath } from '@/config/faq';
import { normalizePathname } from '@/config/seo';
import { seoMeta } from '@/config/seoMeta';

/** 1200x630 logo card (public/og-image.jpg): og:image, twitter:image and the business image. */
export const OG_IMAGE_PATH = '/og-image.jpg';

export const OG_IMAGE_ALT =
  'Alliance Health Physicians — physician-owned excellence in hospital medicine.';

/**
 * The business node: a MedicalOrganization (site brief) that is also a MedicalBusiness, the
 * LocalBusiness subtype that makes address, geo, opening hours and telephone valid on it.
 * No Physician nodes: the site names no providers (see the site plan's questions).
 */
type BusinessNode = MergeLeafTypes<[MedicalOrganizationLeaf, MedicalBusinessLeaf]>;
type GraphNode = Exclude<Thing, string> | BusinessNode;

export type SchemaPayload = { '@context': 'https://schema.org'; '@graph': GraphNode[] };

function pageUrl(site: string, path: string): string {
  if (!path || path === '/') return site;
  return `${site}${path.startsWith('/') ? path : `/${path}`}`;
}

/** Title up to " | Brand" (or the whole title): the page's short name for breadcrumbs. */
function shortName(title: string): string {
  const pipe = title.indexOf(' | ');
  return pipe > 0 ? title.slice(0, pipe).trim() : title;
}

/** Home > [parent segments that are routes in seoMeta] > page. */
function buildBreadcrumb(path: string, name: string, site: string): BreadcrumbList | null {
  if (path === '/') return null;
  const items: ListItem[] = [{ '@type': 'ListItem', position: 1, name: 'Home', item: site }];
  const segments = path.split('/').filter(Boolean);
  segments.forEach((_, i) => {
    const p = `/${segments.slice(0, i + 1).join('/')}`;
    const last = i === segments.length - 1;
    const label = last ? name : seoMeta[p]?.title;
    if (label) items.push({ '@type': 'ListItem', position: items.length + 1, name: shortName(label), item: pageUrl(site, p) });
  });
  return { '@type': 'BreadcrumbList', itemListElement: items };
}

function buildOrganization(site: string, imageAbs: string): BusinessNode {
  return {
    '@type': ['MedicalOrganization', 'MedicalBusiness'],
    '@id': `${site}/#organization`,
    name: business.brandName,
    legalName: business.legalName,
    url: site,
    logo: imageAbs,
    image: imageAbs,
    description: organizationStructuredDescription,
    telephone: business.phoneE164,
    faxNumber: business.faxDisplay,
    email: business.email,
    address: {
      '@type': 'PostalAddress',
      streetAddress: business.addressLine1,
      addressLocality: business.addressLocality,
      addressRegion: business.addressRegion,
      postalCode: business.postalCode,
      addressCountry: business.addressCountry,
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: business.geo.latitude,
      longitude: business.geo.longitude,
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: [...business.openingHours.dayOfWeek],
        opens: business.openingHours.opens,
        closes: business.openingHours.closes,
      },
    ],
    areaServed: { '@type': 'AdministrativeArea', name: 'Michigan' },
    // The former medicalSpecialty strings ("Hospital Medicine", ...) are not values of
    // schema.org's MedicalSpecialty enumeration; the description carries the service lines.
    ...(business.sameAs.length > 0 ? { sameAs: [...business.sameAs] } : {}),
  };
}

export function buildSchemaGraph(args: {
  path: string;
  title: string;
  description: string;
  siteUrl: string;
  imageUrl: string;
}): GraphNode[] {
  const path = normalizePathname(args.path);
  const site = args.siteUrl.replace(/\/+$/, '');
  const canonical = pageUrl(site, path);
  const organizationId = `${site}/#organization`;
  const websiteId = `${site}/#website`;

  const webPage: WebPage = {
    '@type': 'WebPage',
    '@id': canonical,
    url: canonical,
    name: args.title,
    description: args.description,
    inLanguage: 'en-US',
    isPartOf: { '@id': websiteId },
    about: { '@id': organizationId },
  };
  const webSite: WebSite = {
    '@type': 'WebSite',
    '@id': websiteId,
    url: site,
    name: business.brandName,
    inLanguage: 'en-US',
    publisher: { '@id': organizationId },
  };
  const graph: GraphNode[] = [buildOrganization(site, args.imageUrl), webPage, webSite];

  const faqs = faqsByPath[path];
  if (faqs?.length) {
    const faqPage: FAQPage = {
      '@type': 'FAQPage',
      '@id': `${canonical}#faq`,
      isPartOf: { '@id': canonical },
      mainEntity: faqs.map(({ q, a }) => ({ '@type': 'Question', name: q, acceptedAnswer: { '@type': 'Answer', text: a } })),
    };
    graph.push(faqPage);
  }

  const breadcrumb = buildBreadcrumb(path, args.title, site);
  if (breadcrumb) graph.push(breadcrumb);

  return graph;
}

export function buildSchemaPayload(args: Parameters<typeof buildSchemaGraph>[0]): SchemaPayload {
  return { '@context': 'https://schema.org', '@graph': buildSchemaGraph(args) };
}
